import { Modifications as ModificationsType } from '@src/types/graphql-types'
import useFormikHelper from '../../useFormikHelper'
import useTubeValidation from '../../useTubeValidation'
import {
  FormControlLabel,
  Grid,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import messages from '../../../../messages'
import { FormattedMessage } from 'react-intl'
import FeasibilityMessage from './FeasibilityMessage'
import { ModificationTypes, ModificationValues } from '../../enum'
import {
  insertAsteriskBetweenCharacters,
  useProductFromQueryParam,
} from '../../ProductsConfiguration'
import { splitMessages } from '../../utils'
import ValidationErrors from '@src/routes/Configurators/validations/ValidationErrors'
import { TubeProduct } from '../../../../utils/StandardMTOEnum'
import { BiTubeProduct } from '../../../BiTube/enum'

interface FiveOrThreeModificationsType {
  disabled: boolean
  modificationType: string
  modData?: ModificationsType
}

const FiveOrThreeModifications: React.FC<FiveOrThreeModificationsType> = ({
  disabled,
  modificationType,
  modData,
}) => {
  const formik = useFormikHelper()
  const product = useProductFromQueryParam()

  const { validationAPI } = useTubeValidation()

  let defaultSelectionValue =
    formik.selectedOligoValues[modificationType] || ModificationValues.None
  if (disabled) {
    defaultSelectionValue = ''
  }

  const onModChange = (value: string) => {
    const modificationValue = value === ModificationValues.None ? null : value
    formik.setOligoFieldValue(modificationType, modificationValue, false)
    if (value === ModificationValues.None) {
      // For Three mod remove * from End of Sequence if sent to None
      // For Five mod remove * from Beginning of Sequence if sent to None
      const regEx =
        modificationType === ModificationTypes.ThreeMod ? /\*$/ : /^\*+/
      const sequenceString = formik.selectedOligoValues.sequence.replace(
        regEx,
        ''
      )
      formik.setFieldTouched('sequence', true, false)
      formik.setOligoFieldValue('sequence', sequenceString, true)
    } else if (formik.selectedOligoValues.insertAsterisk) {
      const sequenceString = insertAsteriskBetweenCharacters(
        formik.selectedOligoValues.sequence,
        modificationType === ModificationTypes.FiveMod ||
          !!formik.selectedOligoValues.fiveMod,
        modificationType === ModificationTypes.ThreeMod ||
          !!formik.selectedOligoValues.threeMod
      )
      formik.setFieldTouched('sequence', true, false)
      formik.setOligoFieldValue('sequence', sequenceString, false)
    }
    validationAPI({ ...formik.validationFields, [modificationType]: value })
  }

  const fiveModErrorList =
    (formik.errors?.fiveMod && splitMessages(formik.errors.fiveMod)) || []

  const threeModErrorList =
    (formik.errors?.threeMod && splitMessages(formik.errors.threeMod)) || []

  const antisenceFiveModErrorList =
    (formik.errors?.antisenceFiveMod &&
      splitMessages(formik.errors.antisenceFiveMod)) ||
    []

  const antisenceThreeModErrorList =
    (formik.errors?.antisenceThreeMod &&
      splitMessages(formik.errors.antisenceThreeMod)) ||
    []

  return (
    <div>
      <RadioGroup
        aria-label={`${modificationType}-modifications`}
        name={modificationType}
        onChange={(e) => onModChange(e.target.value)}
        value={defaultSelectionValue}
        row
      >
        {modData?.category[0].modifications.map((mod) => (
          <FormControlLabel
            control={<StyledRadio isConfiguratorsRedesign="true" />}
            label={
              <Typography>
                <FormattedMessage {...messages[mod.displayName]} />
              </Typography>
            }
            value={mod.value}
            disabled={disabled}
            key={mod.value}
          />
        ))}
      </RadioGroup>
      {formik.allValues.hasTriedNextStep &&
        modificationType === ModificationTypes.FiveMod &&
        fiveModErrorList.length > 0 && (
          <ValidationErrors errors={fiveModErrorList} configuratorsRedesign />
        )}
      {formik.allValues.hasTriedNextStep &&
        modificationType === ModificationTypes.ThreeMod &&
        threeModErrorList.length > 0 && (
          <ValidationErrors errors={threeModErrorList} configuratorsRedesign />
        )}
      {formik.allValues.hasTriedNextStep &&
        modificationType === ModificationTypes.AntisenceFiveMod &&
        antisenceFiveModErrorList.length > 0 && (
          <ValidationErrors
            errors={antisenceFiveModErrorList}
            configuratorsRedesign
          />
        )}
      {formik.allValues.hasTriedNextStep &&
        modificationType === ModificationTypes.AntisenceThreeMod &&
        antisenceThreeModErrorList.length > 0 && (
          <ValidationErrors
            errors={antisenceThreeModErrorList}
            configuratorsRedesign
          />
        )}
      {modificationType === ModificationTypes.FiveMod &&
        product === BiTubeProduct.mirna && (
          <Grid container style={{ marginTop: '12px' }}>
            <Typography
              component="span"
              variant="body1"
              data-scrub
              style={{ fontSize: '12px' }}
            >
              <FormattedMessage
                id="CONF_MIRNA_FIVE_MODIFICATION_HELP_TEXT"
                defaultMessage="The 5' end of the sense strand must have a modification.  We recommend Amino-Modifier-C6 [AmC6] as this modification has been shown to help facilitate proper loading of the antisense (guide) strand into RISC."
              />
            </Typography>
          </Grid>
        )}
      {product !== TubeProduct.Wellred &&
        product !== BiTubeProduct.Duplex &&
        product !== BiTubeProduct.mirna && <FeasibilityMessage />}
    </div>
  )
}

export default FiveOrThreeModifications
